import { useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import CollectorLegendControl from '@App/components/Map/controls/CollectorLegendControl/CollectorLegendControl';
import PanelHeader from '@App/components/PanelHeader/PanelHeader';
import PanelList, {
  getPropertyRecord,
} from '@App/components/PanelList/PanelList';
import { useCollectorDetails } from '@services/query.service';
import { getFeaturesBounds } from '@utils/geometry.utils';

import { layers, sources } from '../sources.layers';

import { inactiveSourceLayers } from './collector.layers';

import './collector.css';

function Collector() {
  const { map } = useOutletContext();
  const { collectorId } = useParams();
  const navigate = useNavigate();
  const { data } = useCollectorDetails(collectorId);
  const record = getPropertyRecord(data);
  const name = record.Naam || record.naam || record.name || 'Collector';

  useEffect(() => {
    if (!map) return undefined;

    const legendControl = new CollectorLegendControl({
      'potential': 900,
      'heat-delivered': 200,
    });
    map.addControl(legendControl, 'bottom-right');

    return () => {
      map.removeControl(legendControl);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !data?.geometry) return undefined;

    const featureBounds = getFeaturesBounds([data]);
    if (!featureBounds) return undefined;

    const frame = requestAnimationFrame(() => {
      map.resize();
      map.fitBounds(featureBounds, {
        padding: {
          top: 48,
          right: 48,
          bottom: 48,
          left: 450,
        },
        maxZoom: 16,
        duration: 1000,
      });
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [data, map]);

  useEffect(() => {
    if (!map) return undefined;

    const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;
    if (!s3BucketUrl) return undefined;

    const sourceEntries = Object.values(sources);
    const layerEntries = Object.values(layers);
    sourceEntries.forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, {
          type: source.type,
          url: `pmtiles://${s3BucketUrl}/${source.pmtilesFile}`,
          promoteId: source.promoteId,
        });
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;
    layerEntries.forEach((layer) => {
      if (!map.getLayer(layer.id)) map.addLayer(layer, layerAfterRoadLabelsId);
    });

    Object.entries(inactiveSourceLayers).forEach(([layerName, paintProperties]) => {
      Object.entries(paintProperties).forEach(([property, value]) => {
        map.setPaintProperty(layers[layerName].id, property, value);
      });
    });

    const navigationTargets = {
      [layers.flowingWaterHitArea.id]: feature => `/bronnen/stromend-water/${feature.id}`,
      [layers.stillWater.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.stillWaterOutline.id]: feature => `/bronnen/stilstaand-water/${feature.id}`,
      [layers.rwzi.id]: feature => `/bronnen/rwzi/${feature.id}`,
    };
    const clickHandlers = Object.entries(navigationTargets).map(([layerId, getPath]) => {
      const handler = (event) => {
        const feature = event.features?.[0];
        if (feature?.id !== undefined && feature?.id !== null) {
          navigate(getPath(feature));
        }
      };
      map.on('click', layerId, handler);
      return { layerId, handler };
    });

    const interactiveLayerIds = Object.keys(navigationTargets);
    let hoveredFeature;
    const clearHoveredFeature = () => {
      if (!hoveredFeature) return;
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = undefined;
    };
    const handleMouseMove = (event) => {
      const feature = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      })[0];
      if (!feature) {
        clearHoveredFeature();
        map.getCanvas().style.cursor = '';
        return;
      }

      const featureStateTarget = {
        source: feature.source,
        sourceLayer: feature.sourceLayer,
        id: feature.id,
      };
      if (
        featureStateTarget.id !== undefined
        && featureStateTarget.id !== null
        && (
          !hoveredFeature
          || hoveredFeature.id !== featureStateTarget.id
          || hoveredFeature.source !== featureStateTarget.source
        )
      ) {
        clearHoveredFeature();
        hoveredFeature = featureStateTarget;
        map.setFeatureState(hoveredFeature, { hover: true });
      }
      map.getCanvas().style.cursor = 'pointer';
    };
    map.on('mousemove', handleMouseMove);

    return () => {
      clickHandlers.forEach(({ layerId, handler }) => map.off('click', layerId, handler));
      map.off('mousemove', handleMouseMove);
      clearHoveredFeature();
      map.getCanvas().style.cursor = '';
      [...layerEntries].reverse().forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      [...sourceEntries].reverse().forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [map, navigate]);

  return (
    <div className="collector">
      <PanelHeader
        map={map}
        rootPath="/bronnen"
        currentName={name}
      />
      <PanelList data={data} />
    </div>
  );
}

export default Collector;
